import {
  EPushNoticationPublishType,
  ICommonSettings,
  IImageLibraryDto,
  IPushNotificationSettingWeb
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { ReactNode } from "react";
import { ImageLibraryService } from "../../../../../services/image-library-service";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigPushNotificationWebState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingPushNotification?: IPushNotificationSettingWeb;
  popoverVisible: boolean;
  allImage?: IImageLibraryDto[];
  imageSearchText: string;
  fileName: string;
  publishType: EPushNoticationPublishType;
}

export interface IConfigPushNotificationWebProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingPushNotificationId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigPushNotificationWeb extends React.PureComponent<
  IConfigPushNotificationWebProps,
  IConfigPushNotificationWebState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigPushNotificationWebProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingPushNotification: undefined,
      popoverVisible: false,
      allImage: [],
      imageSearchText: "",
      fileName: "",
      publishType: EPushNoticationPublishType.Development
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigPushNotificationWebProps & IWithSDProps) {
    if (prevProps.editingPushNotificationId !== this.props.editingPushNotificationId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (this.props.editingPushNotificationId) {
      const pn = this.props.commonSettings.pushNotificationSettingsWeb?.find(
        (x) => x.id === this.props.editingPushNotificationId && !x.isGlobalCommonConfigOnProjectConfig
      );
      this.setState({ editingPushNotification: pn }, () => {
        if (pn) {
          const fieldsValue = this.formRef.current?.getFieldsValue();
          this.formRef.current?.resetFields();

          fieldsValue.pushNotificationName = pn.name;
          fieldsValue.pushNotificationPrivateKey = pn.privateKey;
          fieldsValue.pushNotificationPublicKey = pn.publicKey;

          this.formRef.current?.setFieldsValue(fieldsValue);
        }
      });
    } else {
      this.setState({}, () => {
        window.crypto.subtle
          .generateKey(
            {
              name: "ECDH",
              namedCurve: "P-256"
            },
            true,
            ["deriveKey", "deriveBits"]
          )
          .then((keyPair) => {
            return Promise.all([
              window.crypto.subtle.exportKey("raw", keyPair.publicKey),
              window.crypto.subtle.exportKey("jwk", keyPair.privateKey)
            ]).then(([publicKeyBuffer, jwkPrivate]) => {
              const fieldsValue = this.formRef.current?.getFieldsValue();
              this.formRef.current?.resetFields();

              if (fieldsValue) {
                fieldsValue.pushNotificationPrivateKey = jwkPrivate.d;
                fieldsValue.pushNotificationPublicKey = this.bufferToBase64Url(publicKeyBuffer);

                this.formRef.current?.setFieldsValue(fieldsValue);
              }
            });
          });
      });
    }
  };

  bufferToBase64Url = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }
    const base64 = btoa(binary);
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  };

  existingNameValidator = async (_rule, value) => {
    const setting = this.props.commonSettings.pushNotificationSettingsWeb;

    const isSettingAlreadyExist =
      setting?.some(
        (x) =>
          x.name === value && x.id !== this.props.editingPushNotificationId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Push Notification Setting name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IPushNotificationSettingWeb;
    if (this.props.editingPushNotificationId) {
      obj = {
        id: this.props.editingPushNotificationId,
        name: data.pushNotificationName,
        publicKey: data.pushNotificationPublicKey,
        privateKey: data.pushNotificationPrivateKey
      };
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "pushNotificationSettingsWeb",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      obj = {
        id: Guid.create().toString(),
        name: data.pushNotificationName,
        publicKey: data.pushNotificationPublicKey,
        privateKey: data.pushNotificationPrivateKey
      };
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "pushNotificationSettingsWeb",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deletePushNotification = () => {
    if (!this.props.editingPushNotificationId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingPushNotificationId || "",
      configKey: "pushNotificationSettingsWeb",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Push Notification setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  getImageLibrary = async () => {
    try {
      const res = await ImageLibraryService.GetAll();
      if (res && res.status === 200) {
        this.setState({ allImage: res.data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
    }
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Push notification setting"
        secondHeader="Push notification setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingPushNotificationId ? this.deletePushNotification : undefined}
            deleteTestId="webPushNotificationSettingDeleteButton"
            submitTestId={`webPushNotificationSetting${
              this.props.editingPushNotificationId ? "Update" : "Create"
            }Button`}
            submitLabel={this.props.editingPushNotificationId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configPushNotificationWebForm"
          />
        }
      >
        <Form
          id="configPushNotificationWebForm"
          name="pushNotificationSettingsWeb"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription(
              "pushnotificationsettingspopup_webpushnotificationsettings_description"
            )}
          >
            <ConfigField
              label="Name"
              required
              tooltipContentKey="pushnotificationsettingspopup_pushnotificationsettings_name"
            >
              <Form.Item
                name="pushNotificationName"
                rules={[
                  {
                    required: true,
                    message: "Please enter a Push Notification Name."
                  },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "Push Notification Setting Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="webPushNotificationNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Public Key"
              tooltipContentKey="pushnotificationsettingspopup_pushnotificationsettings_publickey"
            >
              <Form.Item name="pushNotificationPublicKey">
                <KuikaInput size="md" data-testid="webPushNotificationPublicKeyInput" autoFocus readOnly />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Private Key"
              tooltipContentKey="pushnotificationsettingspopup_pushnotificationsettings_privatekey"
            >
              <Form.Item name="pushNotificationPrivateKey">
                <KuikaInput size="md" data-testid="webPushNotificationPrivateKeyInput" autoFocus readOnly />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
